import React from "react";
import { useTVDisplay } from "./hooks/useTVDisplay";
import TVClassCard from "./components/TVClassCard";
import TVMediaCarousel from "./components/TVMediaCarousel";

import bgSwimming from "../../../assets/images/new1Bg.png";
import bgRunning from "../../../assets/images/kiosk_bgs/bg_running_1777982795262.png";
import bgWeightlifting from "../../../assets/images/kiosk_bgs/bg_weightlifting_1777982840440.png";
import bgCrossfit from "../../../assets/images/kiosk_bgs/bg_crossfit_1777982887027.png";

import neoLogo from "../../../assets/images/Neo_Logo_A.svg";
import "moment/locale/pt-br";
import { Spinner } from "reactstrap";
import { Link } from "react-router-dom";

const BACKGROUNDS = {
    'swimming': bgSwimming,
    'running': bgRunning,
    'weightlifting': bgWeightlifting,
    'crossfit': bgCrossfit
};

const TVDisplay = () => {
    document.title = "TV Display | NeoApp";

    const {
        currentSessions,
        currentTime,
        loading,
        tenantLogo,
        currentMedia,
        showMediaOverlay,
        kioskBackground
    } = useTVDisplay();

    const currentBg = (kioskBackground && BACKGROUNDS[kioskBackground]) ? BACKGROUNDS[kioskBackground] : BACKGROUNDS['swimming'];

    const [bgLoaded, setBgLoaded] = React.useState(false);

    React.useEffect(() => {
        const img = new Image();
        img.src = currentBg;
        img.onload = () => {
            setBgLoaded(true);
        };
        img.onerror = () => {
            setBgLoaded(true); // Evita travamento infinito
        };
    }, [currentBg]);

    // Loader unificado do Kiosk para garantir que não pisque a imagem
    if (!bgLoaded || loading) {
        return (
            <div className="d-flex flex-column justify-content-center align-items-center vh-100" style={{ backgroundColor: "#f8f8fb" }}>
                <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
                <h5 className="mt-3" style={{ color: "#495057", fontWeight: "500" }}>Carregando Tela...</h5>
            </div>
        );
    }

    const numCards = currentSessions.length;
    let gridTemplate = "1fr";

    return (
        <React.Fragment>
            {/* ═══════════════════════════════════════════════════════════════ */}
            {/* MODO NORMAL (Base permanente no fundo) */}
            {/* ═══════════════════════════════════════════════════════════════ */}
            <div
                className="d-flex flex-column vh-100 vw-100"
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    backgroundColor: "#2a3042",
                    backgroundImage: `url(${currentBg})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    zIndex: 1,
                    overflowY: "auto",
                    overflowX: "hidden"
                }}
            >
                {/* Conteúdo Principal */}
                <div className="d-flex flex-column h-100 w-100" style={{ zIndex: 1, position: "relative" }}>
                    
                    {/* ═══ Header Premium - Faixa Top Responsiva ═══ */}
                    <div
                        className="d-flex justify-content-between align-items-center position-sticky top-0 w-100 gap-2 gap-sm-3 flex-nowrap"
                        style={{
                            zIndex: 50,
                            boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
                            backgroundColor: "rgba(0, 0, 0, 0.85)",
                            borderBottom: "1px solid rgba(255,255,255,0.1)",
                            padding: "clamp(1.5vh, 2vw, 3vh) clamp(1rem, 4vw, 3rem)"
                        }}
                    >
                        <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
                            {tenantLogo && (
                                <img
                                    src={tenantLogo}
                                    alt="Logo Academia"
                                    className="me-2 me-sm-4"
                                    style={{ maxHeight: "clamp(25px, 5vw, 55px)", maxWidth: "clamp(80px, 15vw, 200px)", objectFit: "contain", filter: "brightness(0) invert(1)", flexShrink: 0 }}
                                />
                            )}
                            <div className="border-start border-light border-opacity-25 ps-2 ps-sm-4 overflow-hidden">
                                <h2 className="mb-0 fw-bold text-white text-truncate" style={{ fontSize: "clamp(1rem, 2.5vw, 2rem)", letterSpacing: "1px", lineHeight: "1.2" }}>
                                    Aulas em Andamento
                                </h2>
                                <p className="mb-0 text-white-50 text-truncate" style={{ fontSize: "clamp(0.75rem, 1.5vw, 1.1rem)" }}>Bem-vindo(a) ao seu treino</p>
                            </div>
                        </div>

                        {/* Digital Clock */}
                        <div className="text-end flex-shrink-0 ms-auto">
                            <h1 className="fw-bolder mb-0 text-white" style={{ fontSize: "clamp(1.4rem, 3.5vw, 2.5rem)", letterSpacing: "1px", lineHeight: "1" }}>
                                {currentTime.format("HH:mm")}
                            </h1>
                            <p className="text-white-50 fw-medium mb-0 text-uppercase text-truncate" style={{ fontSize: "clamp(0.6rem, 1.2vw, 0.9rem)", letterSpacing: "0.5px" }}>
                                {currentTime.locale("pt-BR").format("dddd, D [de] MMMM")}
                            </p>
                        </div>
                    </div>

                    {/* ═══ Body / Grid de Componentes ═══ */}
                    <div className="flex-grow-1 d-flex flex-column justify-content-start" style={{ padding: "clamp(1.5rem, 4vw, 3rem)" }}>
                        {numCards > 0 ? (
                            <div
                                className="w-100 h-100 pb-2"
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: gridTemplate,
                                    gridTemplateRows: `repeat(${numCards}, minmax(0, 1fr))`,
                                    gap: "clamp(12px, 1.5vh, 24px)",
                                    alignItems: "stretch",
                                    height: "100%",
                                }}
                            >
                                {currentSessions.map((session, index) => (
                                    <TVClassCard key={session.id || index} session={session} />
                                ))}
                            </div>
                        ) : null}
                    </div>

                    {/* Powered By (Normal Mode) */}
                    <div className="position-fixed" style={{ bottom: '2vh', right: 'clamp(1rem, 4vw, 2rem)', zIndex: 10 }}>
                        <Link to="/dashboard" className="text-decoration-none">
                            <div className="d-flex align-items-center gap-2 opacity-50" style={{ cursor: "pointer", transition: "all 0.2s ease" }} onMouseEnter={(e) => e.currentTarget.style.opacity = "1"} onMouseLeave={(e) => e.currentTarget.style.opacity = "0.5"}>
                                <span style={{ fontSize: "clamp(9px, 1vw, 12px)", fontWeight: "700", color: "#ffffff", letterSpacing: "1.5px" }}>POWERED BY</span>
                                <img src={neoLogo} alt="NeoApp" style={{ height: "clamp(12px, 1.5vw, 16px)", filter: "brightness(0) invert(1)" }} />
                            </div>
                        </Link>
                    </div>
                </div>
            </div>

            {/* ═══════════════════════════════════════════════════════════════ */}
            {/* MODO IDLE — OVERLAY DE MÍDIA (fade suave sobre o modo Normal) */}
            {/* Alterna: Mídia (duração variável) → Aulas (30s) → Próxima Mídia → ... */}
            {/* ═══════════════════════════════════════════════════════════════ */}
            <div
                className="vh-100 vw-100"
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    backgroundColor: "#000",
                    zIndex: 9999,
                    overflow: "hidden",
                    cursor: "none",
                    opacity: showMediaOverlay ? 1 : 0,
                    pointerEvents: showMediaOverlay ? "auto" : "none",
                    transition: "opacity 1s ease-in-out"
                }}
            >
                {/* Exibe a mídia atual — single mostra 1 imagem, carousel roda os slides */}
                {currentMedia && (
                    <TVMediaCarousel media={currentMedia} />
                )}

                {/* Powered By (Idle Mode) */}
                <div
                    style={{
                        position: "absolute",
                        bottom: '2vh',
                        right: 'clamp(1rem, 4vw, 2rem)',
                        zIndex: 20,
                        opacity: 0.5,
                    }}
                >
                    <div className="d-flex align-items-center gap-2">
                        <span style={{ fontSize: "clamp(9px, 1vw, 12px)", fontWeight: "700", color: "#fff", letterSpacing: 1.5 }}>POWERED BY</span>
                        <img src={neoLogo} alt="NeoApp" style={{ height: "clamp(12px, 1.5vw, 16px)", filter: "brightness(0) invert(1)" }} />
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default TVDisplay;
